import type { ApolloError } from "@apollo/client/errors";
import { find, mapObjIndexed, sort, values } from "ramda";
import { useEffect, useState } from "react";
import { match } from "ts-pattern";
import { sortAlpha } from "widgets/utils";

import {
  type ProjectBasisFragment,
  useGetAccessibleProjectGroupsLazyQuery,
} from "@/modules/projects/graphql/projects-queries.gql";
import { extractAllProjectsFromGroups } from "@/modules/projects/utils/extractAllProjectsFromGroups";
import { extractPrivateGroups } from "@/modules/projects/utils/extractPrivateGroup";
import { getProjectsByUserGroups } from "@/modules/projects/utils/getProjectsByUserGroups";
import { getProjectLabel } from "@/redux/modules/config.ts";

export interface List {
  name: string;
  items: ProjectBasisFragment[];
}

export interface ProjectsFetchOptions {
  includeStagedProjects?: boolean;
  includeAllProjects?: boolean;
  initialSelectedProjectId?: number | null;
  defaultSelection?: "private" | "previous" | "all" | null;
  onSelectionChange?: (project: ProjectBasisFragment | undefined) => void;
}

export interface Result {
  projectsList: ProjectBasisFragment[] | null;
  projectsTree: List[];
  error?: ApolloError;
  isLoading: boolean;
  defaultSelectedProject: ProjectBasisFragment | null;
}

const allProjectsItem = {
  id: -1,
  name: `All ${getProjectLabel(true, true)}`,
} satisfies Partial<ProjectBasisFragment>;

export const useFetchProjects = ({
  includeStagedProjects,
  includeAllProjects,
  defaultSelection,
}: Pick<ProjectsFetchOptions, "includeAllProjects" | "includeStagedProjects" | "defaultSelection">): Result => {
  const [projectsList, setProjectsList] = useState<ProjectBasisFragment[] | null>(null);
  const [projectsTree, setProjectsTree] = useState<List[]>([]);
  const [privateProject, setPrivateProject] = useState<ProjectBasisFragment | null>(null);
  const [lastSavedTarget, setLastSavedTarget] = useState<number | null>(null);
  const [defaultSelectedProject, setDefaultSelectedProject] = useState<ProjectBasisFragment | null>(null);

  const [fetch, { error, loading: isLoading }] = useGetAccessibleProjectGroupsLazyQuery({
    variables: { includeStagedProjects },
  });

  useEffect(() => {
    async function load() {
      const { data } = await fetch();
      const groups = data?.userGroup?.accessibleUserGroups;
      const allProjects = extractAllProjectsFromGroups(groups);

      if (includeAllProjects) {
        allProjects?.push(allProjectsItem as ProjectBasisFragment);
      }

      if (groups && allProjects) {
        const _groups = getProjectsByUserGroups(groups);
        const [privateGroups, otherGroups] = extractPrivateGroups(_groups);

        // We expect to have a single Private Workspace with a single Project
        const privateProject = privateGroups ? (values(privateGroups)?.[0]?.[0] ?? null) : null;
        const otherItems = values(mapObjIndexed((value, key) => ({ name: key, items: value }), otherGroups ?? {}));
        const sortedItems = sort((a, b) => sortAlpha(a?.name ?? "", b?.name ?? ""), otherItems);

        setLastSavedTarget(data.userParams?.lastSaveTarget ?? null);
        setProjectsList(allProjects);
        setPrivateProject(privateProject);
        setProjectsTree(
          privateProject || includeAllProjects
            ? [
                {
                  name: "My Places",
                  items:
                    privateProject && includeAllProjects
                      ? [allProjectsItem as ProjectBasisFragment, privateProject]
                      : privateProject
                        ? [privateProject]
                        : [allProjectsItem as ProjectBasisFragment],
                },
                ...sortedItems,
              ]
            : sortedItems,
        );
      }
    }

    void load();
  }, [fetch, includeAllProjects]);

  useEffect(() => {
    match(defaultSelection)
      .with("private", () => {
        if (privateProject) {
          setDefaultSelectedProject(privateProject);
        }
      })
      .with("previous", () => {
        if (lastSavedTarget && projectsList) {
          const previousProject = find((project) => project.id === lastSavedTarget, projectsList);

          if (previousProject) {
            setDefaultSelectedProject(previousProject);
          }
        }
      })
      .with("all", () => {
        setDefaultSelectedProject(allProjectsItem as ProjectBasisFragment);
      })
      .with(undefined, () => {
        /* nothing to do */
      })
      .with(null, () => {
        /* nothing to do */
      })
      .exhaustive();
  }, [lastSavedTarget, privateProject, projectsList, defaultSelection]);

  return { projectsList, projectsTree, error, isLoading, defaultSelectedProject };
};
